import { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import {
  detectTextDelimiter,
  isPlainTextPricelistFile,
} from '@erp/shared-utils';
import type { SheetData, CellData, CellFormat } from './types';

/**
 * The most data rows a preview parses per sheet. Parsing is the cost of a
 * preview, so a larger file shows its first rows with a notice to download it.
 */
export const PREVIEW_ROW_LIMIT = 10_000;

/** One character's width in the preview's mono font at zoom 1, in px. */
export const PREVIEW_CHAR_PX = 7.2;

interface UseXlsxParserResult {
  sheets: SheetData[];
  isLoading: boolean;
  error: string | null;
}

// Color type for xlsx cell styles (not exported by xlsx types)
interface XlsxColor {
  rgb?: string;
  theme?: number;
  tint?: number;
}

function getCellRef(row: number, col: number): string {
  let colStr = '';
  let c = col;
  while (c >= 0) {
    colStr = String.fromCharCode((c % 26) + 65) + colStr;
    c = Math.floor(c / 26) - 1;
  }
  return `${colStr}${row + 1}`;
}

function parseColor(color: XlsxColor | undefined): string | undefined {
  if (!color) return undefined;
  if (color.rgb) {
    // xlsx returns RGB without # prefix, and sometimes with alpha (ARGB)
    const rgb = color.rgb.length === 8 ? color.rgb.slice(2) : color.rgb;
    return `#${rgb}`;
  }
  return undefined;
}

function extractCellFormat(
  cell: XLSX.CellObject | undefined,
): CellFormat | undefined {
  if (!cell?.s) return undefined;

  const style = cell.s;

  const format: CellFormat = {};
  let hasFormat = false;

  if (style.font?.bold) {
    format.bold = true;
    hasFormat = true;
  }
  if (style.font?.italic) {
    format.italic = true;
    hasFormat = true;
  }
  if (style.font?.color) {
    const textColor = parseColor(style.font.color);
    if (textColor && textColor !== '#000000') {
      format.textColor = textColor;
      hasFormat = true;
    }
  }
  if (style.fill?.fgColor) {
    const bgColor = parseColor(style.fill.fgColor);
    if (bgColor && bgColor !== '#FFFFFF' && bgColor !== '#ffffff') {
      format.bgColor = bgColor;
      hasFormat = true;
    }
  }

  return hasFormat ? format : undefined;
}

function getCellType(cell: XLSX.CellObject | undefined): CellData['type'] {
  if (!cell) return 'empty';

  switch (cell.t) {
    case 'n':
      return 'number';
    case 'd':
      return 'date';
    case 'b':
      return 'boolean';
    case 's':
      return 'string';
    default:
      return 'string';
  }
}

function getCellValue(
  cell: XLSX.CellObject | undefined,
): string | number | null {
  if (!cell) return null;

  // Use formatted value if available, otherwise raw value
  if (cell.w !== undefined) return String(cell.w);
  if (cell.v === undefined || cell.v === null) return null;

  // Format dates nicely
  if (cell.t === 'd' && cell.v instanceof Date) {
    return cell.v.toLocaleDateString();
  }

  if (typeof cell.v === 'number') return cell.v;
  if (typeof cell.v === 'string') return cell.v;
  if (typeof cell.v === 'boolean') return cell.v ? 'TRUE' : 'FALSE';

  return String(cell.v);
}

function parseWorksheet(
  worksheet: XLSX.WorkSheet,
  sheetName: string,
): SheetData {
  const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
  const numCols = range.e.c - range.s.c + 1;
  const numRows = range.e.r - range.s.r + 1;

  // Extract headers from first row
  const headers: string[] = [];
  for (let col = 0; col < numCols; col++) {
    const cellRef = getCellRef(range.s.r, range.s.c + col);
    const cell = worksheet[cellRef];
    headers.push(cell?.v?.toString() || XLSX.utils.encode_col(col));
  }

  // Extract data rows (skip header row)
  const rows: CellData[][] = [];
  for (let row = 1; row < numRows; row++) {
    const rowData: CellData[] = [];
    let hasData = false;

    for (let col = 0; col < numCols; col++) {
      const cellRef = getCellRef(range.s.r + row, range.s.c + col);
      const cell = worksheet[cellRef];
      const value = getCellValue(cell);

      if (value !== null && value !== '') {
        hasData = true;
      }

      rowData.push({
        value,
        type: getCellType(cell),
        format: extractCellFormat(cell),
      });
    }

    // Skip completely empty rows
    if (hasData) {
      rows.push(rowData);
    }
  }

  const excelColWidths = Array.isArray(worksheet['!cols'])
    ? worksheet['!cols']
    : undefined;

  const columnWidths = headers.map((header, colIndex) => {
    const excelCol = excelColWidths?.[colIndex];
    // A file's width counts characters of its own font's digits: as many of
    // the preview's (wider, mono) characters, so what fits the file fits here.
    const chars = excelCol?.wch ?? excelCol?.width;
    if (chars) {
      return Math.min(
        400,
        Math.max(60, Math.ceil(chars * PREVIEW_CHAR_PX) + 17),
      );
    }
    if (excelCol?.wpx) {
      return Math.min(400, Math.max(60, excelCol.wpx));
    }

    let maxLen = header.length;
    for (const row of rows.slice(0, 100)) {
      const cellValue = row[colIndex]?.value;
      if (cellValue !== null) {
        maxLen = Math.max(maxLen, String(cellValue).length);
      }
    }
    return Math.min(300, Math.max(60, maxLen * 7 + 16));
  });

  const expandedColumnWidths = headers.map((header, colIndex) => {
    let maxLen = header.length;
    for (const row of rows) {
      const cellValue = row[colIndex]?.value;
      if (cellValue !== null) {
        maxLen = Math.max(maxLen, String(cellValue).length);
      }
    }
    return Math.max(60, maxLen * 7 + 16);
  });

  return {
    name: sheetName,
    headers,
    rows,
    columnWidths,
    expandedColumnWidths,
    ...sheetExtent(worksheet, range),
  };
}

/** A sheet's data rows in the file, and whether a row limit left some unread. */
function sheetExtent(
  worksheet: XLSX.WorkSheet,
  parsed: XLSX.Range,
): Pick<SheetData, 'totalRows' | 'truncated'> {
  // With a row limit SheetJS keeps the sheet's real extent in `!fullref`.
  const full = worksheet['!fullref']
    ? XLSX.utils.decode_range(worksheet['!fullref'])
    : parsed;
  return {
    totalRows: Math.max(0, full.e.r - full.s.r),
    truncated: full.e.r > parsed.e.r,
  };
}

/** A CSV file is read as text, cell for cell as written: "00123" stays "00123". */
function isCsv(fileName: string | undefined): boolean {
  return /\.csv$/i.test(fileName ?? '');
}

export const PLAIN_TEXT_UNSUPPORTED_FORMAT = 'Unsupported file format.';

function readPlainTextWorkbook(
  bytes: ArrayBuffer,
  sheetRows: number,
): XLSX.WorkBook {
  const delimiter = detectTextDelimiter(new Uint8Array(bytes));
  if (delimiter == null) {
    throw new Error(PLAIN_TEXT_UNSUPPORTED_FORMAT);
  }
  const text = new TextDecoder('utf-8').decode(bytes);
  return XLSX.read(text, {
    type: 'string',
    raw: true,
    FS: delimiter,
    sheetRows,
  });
}

function readWorkbook(
  bytes: ArrayBuffer,
  fileName: string | undefined,
  sheetRows: number,
  mimeType?: string,
  previewPlainTextAsTable = false,
): XLSX.WorkBook {
  if (isCsv(fileName)) {
    // Decoded as UTF-8 first: the decoder drops the byte order mark.
    return XLSX.read(new TextDecoder('utf-8').decode(bytes), {
      type: 'string',
      raw: true,
      sheetRows,
    });
  }
  if (previewPlainTextAsTable && isPlainTextPricelistFile(fileName, mimeType)) {
    return readPlainTextWorkbook(bytes, sheetRows);
  }
  return XLSX.read(bytes, {
    type: 'array',
    cellStyles: true,
    cellDates: true,
    sheetRows,
  });
}

/**
 * Parses an xlsx or CSV file's bytes into sheets for the viewer, the first
 * row of each as its headers, reading at most `rowLimit` data rows per sheet.
 */
export function parseSpreadsheet(
  bytes: ArrayBuffer,
  fileName?: string,
  rowLimit: number = PREVIEW_ROW_LIMIT,
  mimeType?: string,
  previewPlainTextAsTable = false,
): SheetData[] {
  const sheetRows = rowLimit + 1;
  const workbook = readWorkbook(
    bytes,
    fileName,
    sheetRows,
    mimeType,
    previewPlainTextAsTable,
  );
  return workbook.SheetNames.map((sheetName) =>
    parseWorksheet(workbook.Sheets[sheetName], sheetName),
  );
}

/** Fetches a spreadsheet file and parses it for the viewer. */
async function fetchSpreadsheet(
  url: string,
  fileName: string | undefined,
  signal: AbortSignal,
  mimeType?: string,
  previewPlainTextAsTable = false,
): Promise<SheetData[]> {
  const response = await fetch(url, { signal });
  if (!response.ok) throw new Error(`Failed to fetch file: ${response.status}`);
  return parseSpreadsheet(
    await response.arrayBuffer(),
    fileName,
    undefined,
    mimeType,
    previewPlainTextAsTable,
  );
}

function readError(err: unknown): string {
  return err instanceof Error ? err.message : 'Failed to read spreadsheet file';
}

export function useXlsxParser(
  url: string,
  fileName?: string,
  mimeType?: string,
  previewPlainTextAsTable = false,
): UseXlsxParserResult {
  const [sheets, setSheets] = useState<SheetData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!url) {
      setSheets([]);
      setError(null);
      setIsLoading(false);
      return;
    }

    const abortController = new AbortController();
    const { signal } = abortController;
    setIsLoading(true);
    setError(null);
    fetchSpreadsheet(
      url,
      fileName,
      signal,
      mimeType,
      previewPlainTextAsTable,
    ).then(
      (parsed) => {
        if (signal.aborted) return;
        setSheets(parsed);
        setIsLoading(false);
      },
      (err: unknown) => {
        if (signal.aborted) return;
        console.error('Error parsing xlsx:', err);
        setError(readError(err));
        setIsLoading(false);
      },
    );
    return () => abortController.abort();
  }, [url, fileName, mimeType, previewPlainTextAsTable]);

  return { sheets, isLoading, error };
}
